<template>
  <ValidationObserver ref="wizardPage2" id="wizardPage2" name="wizardPage2">
    <div class="pb-5" data-wizard-type="step-content" data-wizard-state="current">
      <div class="mb-10 font-weight-bold text-dark" v-if="formData && referenceData">

        <!-- begin: Insured Entity Details  -->
        <h3>INSURED ENTITY</h3>
        <b-card border-variant="primary" class="mb-5 mt-3" body-class="px-7 py-5">
          <insured-entity :legal-entity="formData.insuredEntity" :is-read-only="isReadOnly" :reference="'insured'"
                          :show-abn-search="showAbnSearch" :show-address="showInsuredAddressSection" :is-uw-mode="isUwMode">
          </insured-entity>

          <!-- NSW Stamp Duty Exemption -->
          <div class="form-group row">
            <div class="form-group col-12 mb-0">
              <label class="col-form-label text-right">
                <strong>Is your business exempt from NSW Stamp Duty?<span v-if="!isUwMode" class="text-danger">*</span>
                  <i id="nswSdIcon" class="fas fa-question-circle text-primary ml-2"
                     v-b-popover.hover.right="nswSdTooltipData"
                  ></i>
                </strong>
              </label>
              <b-form-group class="mb-1">
                <ValidationProvider :rules="{ required: !isUwMode }" name="businessInfo.businessExemptFromStampDuty" v-slot="{ errors }">
                  <b-form-radio-group
                      id="radio-group-business-exempt-from-stamp-duty" name="businessInfo.businessExemptFromStampDuty"
                      v-model="formData.businessInfo.businessExemptFromStampDuty" type="radio" :disabled="isReadOnly"
                  >
                    <b-form-radio type="radio" value="Yes">Yes</b-form-radio>
                    <b-form-radio type="radio" value="No">No</b-form-radio>
                  </b-form-radio-group>
                  <span class="text-danger">{{ errors[0] }}</span>
                </ValidationProvider>
              </b-form-group>
              <div v-if="formData.businessInfo.businessExemptFromStampDuty === 'Yes'">
                To obtain Stamp Duty exemption, the declaration form must be completed and returned prior to the inception
                of cover. Declaration form will be sent to you separately by email.
              </div>
            </div>
          </div>
          <!-- End of NSW Stamp Duty Exemption -->

        </b-card>
        <!-- end: Insured Entity Details  -->

        <!-- begin: Additional Insured Entities -->
        <h3>ADDITIONAL INSUREDS</h3>
        <b-card border-variant="primary" class="mb-5 mt-3" body-class="px-7 py-5">
          <table>
            <thead>
              <th style="width: 20%"></th>
            </thead>
            <tr v-for="(legalEntity, index) in formData.additionalInsuredList" :key="index">
              <td>
                <b-card class="mb-4" bg-variant="light">
                  <div class="row">
                    <div class="col-12">
                      <label class="text-left">Legal Entity {{ index + 1 }}</label>
                      <button class="btn btn-danger m-2 float-right" @click.prevent="removeAdditionalInsuredEntry(index)" v-if="!isReadOnly">
                        Remove
                      </button>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-12">
                      <insured-entity :legal-entity="legalEntity" :is-read-only="isReadOnly" :reference="`${index}`" />
                    </div>
                  </div>
                </b-card>
              </td>
            </tr>
          </table>
          <div>
            <button class="btn btn-primary m-2" @click.prevent="addAdditionalInsuredEntry()" v-if="!isReadOnly">
              Add Legal Entity
            </button>
          </div>
        </b-card>
        <!-- end: Additional Insured Entities -->

        <!-- begin: Client Contact Details -->
        <h3>CLIENT CONTACTS</h3>
        <b-card border-variant="primary" class="mb-6" body-class="px-7 py-5">
          <b-card-title class="mb-1">Primary Contact</b-card-title>
          <span v-if="formData.formType === 'Motor Proposal Form' && (formData.brand === 'CRI' || formData.personCompletingForm.type === 'broker')">
            Broker Contact details from the previous page are pre-filled here.
          </span>
          <span v-if="formData.formType === 'Renewal Declaration Form' && (formData.brand === 'CRI' || formData.personCompletingForm.type === 'broker')">
            Primary contact must be a broker contact.
          </span>

          <client-contact :contact-details="formData.clientContacts.primaryContact"
                          :mandatory="formData.brand === 'Rentsure' || formData.formType === 'Motor Proposal Form'"
                          :is-read-only="isReadOnly || formData.brand === 'CRI' || formData.personCompletingForm.type === 'broker'"></client-contact>

          <b-card-title class="mb-1 mt-10">Secondary Contact</b-card-title>
          <client-contact :contact-details="formData.clientContacts.secondaryContact"
                          :is-read-only="isReadOnly"></client-contact>
        </b-card>
        <!-- end: Client Contact Details -->
    </div>
  </div>
  </ValidationObserver>
</template>

<style lang="scss">
</style>

<script>
import ClientContact from "@/view/pages/wizard/motor-proposal-form/components/ClientContact.vue";
import InsuredEntity from "@/view/pages/wizard/motor-proposal-form/components/InsuredEntity.vue";

export default {
  name: "WizardPage2",

  components: {
    ClientContact,
    InsuredEntity
  },

  props: {
    formData: Object,
    referenceData: Object,
    isReadOnly: Boolean,
    isRenewal: Boolean,
    isUwMode: Boolean
  },
  
  data() {
    return {
    };
  },

  computed: {
    nswSdTooltipData: function() {
      return {
        title: 'NSW Insurance Stamp Duty Exemption Declaration',
        html: true,
        customClass: 'nswsd-tooltip',
        content: `<ol>
                    <li>From 1 January 2018 eligible small businesses (generally with less than $2million turnover) will not be required to pay duty on certain types of insurance, including:</li>
                    <li>Commercial vehicle insurance – for a motor vehicle used primarily for business purposes</li>
                    <li>Occupational indemnity insurance (or professional indemnity) – covering liability arising out of the provision of professional or other services (other than medical indemnity insurance)</li>
                    <li>Product and public liability insurance – insurance covering liability for personal injury or property damage occurring in connection with, or arising out of the products or services of, a business.</li>
                  </ol>
                  <strong>What is a small business?</strong><br/>
                  <ol>
                    <li>You are a small business for the current year if:</li>
                    <li>you carry on a business in the current year; and</li>
                    <li>one or both of the following applies:</li>
                    <ul>
                      <li>you carried on a business in the previous year and your aggregated turnover for that previous year was less than $2million;</li>
                      <li>your aggregated turnover for the current year is likely to be less than $2million.</li>
                    </ul>
                  </ol>`
      }
    },

    showInsuredAddressSection: function () {
      if(this.formData) {
        return this.formData.brand !== 'CRI' && this.formData.personCompletingForm.type !== 'broker';
      }
      return false;
    },

    showAbnSearch: function () {
      if(this.isRenewal) {
        return false;
      }
      else if(this.formData.insuredEntity.showAbnSearch === false) {
        return false;
      }
      return true;
    }
  },

  methods: {
    addAdditionalInsuredEntry: function () {
      this.formData.additionalInsuredList.push({});
    },

    removeAdditionalInsuredEntry: function (index) {
      this.formData.additionalInsuredList.splice(index, 1);
    }
  },
};
</script>
